import { t } from "@i18n";
import type { Status } from "../components/Reviews/reviews.interfaces";
import type { ReviewResourceDescriptor } from "../components/Reviews/ReviewsHelper";
import type { TranslatedString } from "@i18n";

/**
 * Shape of {@link defaultSignatureModalState}. Kept as its own exported type because the literal
 * seeds `ChangeFileStatusModal`'s state in FilesInReview/IssuesInReview: `reviewId` really is `null`
 * and both statuses really are `{}` at rest, so the modal state type must accept those.
 */
export interface SignatureModalDefaults {
  show: boolean;
  resources: ReviewResourceDescriptor[];
  reviewId: string | null;
  statusFrom: Partial<Status>;
  statusTo: Partial<Status>;
  requireMandatoryComment: boolean;
}

export const entityTypes = [
  { id: "files", text: "Files" },
  { id: "estimates", text: "Estimates" },
  { id: "issues", text: "Issues" },
  { id: "tasks", text: "Tasks" },
  { id: "schedules", text: "Schedules" },
];

export const CHANGE_REASONS = {
  REMOVE_OPTION: "removeOption",
  CLEAR: "clear",
};

export const defaultReviewData = {
  _id: "",
  name: "",
  reviewId: "",
  description: "",
  createdBy: {
    firstName: "",
    lastName: "",
  },
  createdDate: 131231311123,
  reviewStatus: "Open",
  reviewFlow: {
    name: "First ReviewFlow",
  },
  currentReviewLevel: {
    serial: 0,
    title: "Temp",
  },
};

export const defaultCreateReviewData = {
  name: "",
  description: "",
  reviewFlow: { id: null },
  stage: 1,
  postCreationData: {
    selectedReviewersAtLevel1: [],
    selectedReviewersFromSpace: [],
    message: "",
    _id: null,
  },
};

export const defaultSignatureModalState: SignatureModalDefaults = {
  show: false,
  resources: [],
  reviewId: null,
  statusFrom: {},
  statusTo: {},
  requireMandatoryComment: false,
};

/**
 * KNOWN BUG, preserved verbatim: only `heading` goes through `t()`. `content`, `successToast` and
 * `failureDefaultToast` are raw English literals that reach the modal body and the toast
 * (`showToastNotification({ content })`), so those three strings are never translated. Does the
 * server read them? No — client copy only. Fix: wrap each in `t()` (and add the keys to en.json);
 * asserted as `TranslatedString` here instead of calling `untranslated()` because a call would change
 * the emitted module.
 */
export const markReviewMapper = {
  approve: {
    heading: t("Mark as Reviewed"),
    content: "Are you sure you want to mark as reviewed ?" as TranslatedString,
    successToast: "The review has been approved" as TranslatedString,
    failureDefaultToast: "The review could not be approved" as TranslatedString,
  },
  revoke: {
    heading: t("Revoke Review"),
    content: "Are you sure you want to revoke the selected review ?" as TranslatedString,
    successToast: "The review has been revoked" as TranslatedString,
    failureDefaultToast: "The review could not be revoked" as TranslatedString,
  },
  skip: {
    heading: t("Skip Review"),
    content: "Are you sure you want to skip the selected review ?" as TranslatedString,
    successToast: "The review has been skipped" as TranslatedString,
    failureDefaultToast: "The review could not be skipped" as TranslatedString,
  },
};

export const reviewStageMapper = {
  Draft: {
    name: "Draft",
    indicatorClass: "reviewStageDraftIndicator",
    containerClass: "reviewStageDraft",
  },
  Open: {
    name: "Ongoing",
    indicatorClass: "reviewStageOpenIndicator",
    containerClass: "reviewStageOpen",
  },
  Closed: {
    name: "Closed",
    indicatorClass: "reviewStageClosedIndicator",
    containerClass: "reviewStageClosed",
  },
  Revoked: {
    name: "Revoked",
    indicatorClass: "reviewStageRevokedIndicator",
    containerClass: "reviewStageRevoked",
  },
};

export const reviewStagesList = [
  {
    text: "Draft",
    value: "Draft",
  },
  {
    text: "Ongoing",
    value: "Open",
  },
  {
    text: "Revoked",
    value: "Revoked",
  },
  {
    text: "Closed",
    value: "Closed",
  },
];

export const entityMapper = {
  RESOURCE_TYPE_FILE: {
    args: ["fileVersionId"],
  },
  RESOURCE_TYPE_ESTIMATE: {
    args: ["estimateId", "estimateVersionId"],
  },
  RESOURCE_TYPE_ISSUE: {
    args: ["issueId"],
  },
  RESOURCE_TYPE_TASK: {
    args: ["taskId"],
  },
};

export const defaultReviewNotificationData = {
  selectedReviewersAtLevel1: [],
  selectedReviewersFromSpace: [],
  message: "",
};
